import { type UIMessage } from 'ai';
import { type ConversationalSearchContentClass } from 'search-gateway-client';

/**
 * Bracketed tag prepended to the first message of a new search so the LLM defaults to the modality
 * the search was initiated from, without relying on a structured backend parameter. The leading
 * `[` and the bracketed label make it improbable as the opening of a genuine query, so the strip
 * below can key on the full prefix without clobbering real user text. It is removed on display so
 * the user never sees it.
 */
export const CONTENT_CLASS_PROMPT_PREFIX: Record<ConversationalSearchContentClass, string> = {
    audio: '[Audio Search] ',
    video: '[Video Search] ',
};

const CONTENT_CLASS_PROMPT_PREFIXES: readonly string[] = Object.values(CONTENT_CLASS_PROMPT_PREFIX);

/**
 * Returns a copy of the message with the content-class context prepended to its first text part.
 * Only the outgoing request copy should be modified — the displayed message keeps the raw query.
 */
export function withContentClassPrefix(message: UIMessage, contentClass: ConversationalSearchContentClass): UIMessage {
    const prefix = CONTENT_CLASS_PROMPT_PREFIX[contentClass];
    let prefixed = false;
    const parts = message.parts.map((part) => {
        if (!prefixed && part.type === 'text') {
            prefixed = true;
            return { ...part, text: `${prefix}${part.text}` };
        }
        return part;
    });
    return { ...message, parts };
}

/**
 * Returns a copy of the message with any content-class prefix removed from its first text part.
 * Used for display: messages reloaded from history carry the prefix that was persisted with the
 * outgoing request, but the user should only ever see their raw query.
 */
export function stripContentClassPrefix(message: UIMessage): UIMessage {
    let stripped = false;
    const parts = message.parts.map((part) => {
        if (!stripped && part.type === 'text') {
            stripped = true;
            const prefix = CONTENT_CLASS_PROMPT_PREFIXES.find((p) => part.text.startsWith(p));
            if (prefix) {
                return { ...part, text: part.text.slice(prefix.length) };
            }
        }
        return part;
    });
    return { ...message, parts };
}
